// Certificates.jsx — generic event certificates (result day, sports week, etc.)
// Admin picks an event name + a specific student or a whole class/section,
// and every recipient gets the same reusable branded certificate. Printing
// reuses the exact window.print()-based pattern already used for letters
// (LetterBuilder.jsx buildPrintHTML) — no PDF library needed.
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { AcademicCapIcon, PrinterIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { createLetterBuilder } from './LetterBuilder';

// Always present regardless of which tab is active (unlike `css` below,
// which only mounts while the Event Certificates tab is showing).
const tabCss = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .cp-tabs { display:flex; gap:4px; margin-bottom:1.4rem; background:#f0f2f7; border-radius:11px; padding:3px; max-width:420px; }
  .cp-tab { flex:1; padding:8px 16px; border-radius:9px; border:none; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:.15s; background:transparent; color:#9094a8; }
  .cp-tab.on { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
`;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cp * { box-sizing:border-box; }
  .cp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:cpFade .4s ease both; }
  @keyframes cpFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .cp-header { margin-bottom:1.5rem; }
  .cp-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .cp-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .layout { display:grid; grid-template-columns:400px 1fr; gap:1.4rem; align-items:start; }
  @media (max-width:900px) { .layout { grid-template-columns:1fr; } }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; }
  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#7c3aed; background:white; }
  .target-tabs { display:flex; gap:6px; margin-bottom:14px; }
  .target-tab { flex:1; padding:8px 10px; border-radius:9px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; text-align:center; }
  .target-tab.active { background:#7c3aed; color:white; border-color:#7c3aed; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:10px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; transition:all .18s; width:100%; justify-content:center; }
  .btn svg { width:16px; height:16px; stroke-width:2; }
  .btn-purple { background:#7c3aed; color:white; }
  .btn-purple:hover { background:#6d28d9; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .suggest-list { border:1.5px solid #e0e4f0; border-radius:9px; margin-top:4px; max-height:160px; overflow-y:auto; background:white; }
  .suggest-item { padding:8px 11px; font-size:.8rem; cursor:pointer; border-bottom:1px solid #f3f5fb; }
  .suggest-item:hover { background:#f5f3ff; }
  .picked-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:9px; background:#f5f3ff; border:1px solid #ddd6fe; font-size:.78rem; font-weight:600; color:#5b21b6; }
  .preview-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1rem; }
  .cert-frame { border:10px solid #7c3aed22; border-radius:12px; padding:2.2rem; text-align:center; background:linear-gradient(135deg,#faf9ff,#ffffff); }
  .cert-title { font-family:'Sora',serif; font-size:1.5rem; font-weight:700; color:#5b21b6; letter-spacing:.04em; text-transform:uppercase; }
  .cert-sub { font-size:.78rem; color:#9094a8; margin:6px 0 20px; }
  .cert-name { font-size:1.6rem; font-weight:700; color:#0f1123; margin:14px 0; font-family:'Sora',serif; }
  .cert-body { font-size:.9rem; color:#4a5075; max-width:480px; margin:0 auto; line-height:1.6; }
  .recip-list { max-height:200px; overflow-y:auto; border-top:1px solid #f0f2f8; margin-top:1rem; padding-top:1rem; }
  .recip-row { display:flex; justify-content:space-between; padding:6px 0; font-size:.8rem; }
  .history-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 14px; text-align:center; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .tbl td { padding:10px 14px; font-size:.8rem; text-align:center; border-bottom:1px solid #f3f5fb; }
  .empty { text-align:center; padding:2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:120px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#7c3aed; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
`;

const todayStr = () => new Date().toISOString().slice(0,10);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'long',year:'numeric'}) : '—';

// One reusable branded certificate, printed once per recipient with a page
// break between each — same window.print() approach as LetterBuilder.jsx.
function buildCertificatePrintHTML({ eventName, achievementText, schoolName, issueDate, recipients }) {
  const page = (r) => `
    <div class="cert-page">
      <div class="cert-border">
        <div class="school-name">${schoolName || 'School Name'}</div>
        <div class="cert-heading">Certificate of Achievement</div>
        <div class="cert-event">${eventName}</div>
        <div class="cert-presented">This certificate is proudly presented to</div>
        <div class="cert-recipient">${r.name}</div>
        <div class="cert-class">${r.className ? `Class ${r.className}${r.section ? ' - ' + r.section : ''}` : ''}</div>
        <div class="cert-body">${achievementText || `in recognition of their participation and achievement on the occasion of ${eventName}.`}</div>
        <div class="cert-footer">
          <div class="cert-date">${fmtDate(issueDate)}</div>
          <div class="cert-sig"><div class="sig-line"></div>Principal</div>
        </div>
      </div>
    </div>`;

  return `<!DOCTYPE html><html><head><title>${eventName} — Certificates</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Sora:wght@400;500;600&display=swap');
    * { box-sizing:border-box; }
    body { margin:0; font-family:'Sora',sans-serif; }
    @page { size:landscape; margin:0; }
    .cert-page { width:100vw; height:100vh; display:flex; align-items:center; justify-content:center; page-break-after:always; padding:30px; }
    .cert-page:last-child { page-break-after:auto; }
    .cert-border { width:100%; height:100%; border:3px solid #7c3aed; outline:1px solid #7c3aed; outline-offset:8px; border-radius:6px; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:40px; background:linear-gradient(135deg,#faf9ff,#ffffff); }
    .school-name { font-size:16px; font-weight:600; color:#5b21b6; letter-spacing:.08em; text-transform:uppercase; margin-bottom:6px; }
    .cert-heading { font-family:'Playfair Display',serif; font-size:36px; font-weight:700; color:#0f1123; margin-bottom:6px; }
    .cert-event { font-size:16px; font-weight:600; color:#7c3aed; text-transform:uppercase; letter-spacing:.06em; margin-bottom:26px; }
    .cert-presented { font-size:14px; color:#6b7194; margin-bottom:8px; }
    .cert-recipient { font-family:'Playfair Display',serif; font-size:30px; font-weight:700; color:#0f1123; margin-bottom:4px; border-bottom:2px solid #7c3aed55; display:inline-block; padding:0 20px 8px; }
    .cert-class { font-size:13px; color:#9094a8; margin:8px 0 20px; }
    .cert-body { font-size:14px; color:#4a5075; max-width:520px; line-height:1.7; margin-bottom:30px; }
    .cert-footer { display:flex; justify-content:space-between; align-items:flex-end; width:100%; max-width:480px; margin-top:20px; }
    .cert-date { font-size:13px; color:#6b7194; }
    .cert-sig { font-size:13px; color:#1a1d2e; font-weight:600; }
    .sig-line { width:140px; border-bottom:1px solid #999; margin-bottom:6px; }
  </style></head><body>
    ${recipients.map(page).join('')}
    <script>window.onload = () => { window.print(); };<\/script>
  </body></html>`;
}

export function createCertificatesPage({ apiBase }) {
  const LetterBuilder = createLetterBuilder({ apiBase });
  return function CertificatesPage() {
    const [tab, setTab] = useState('certificates');
    return (
      <div><style>{tabCss}</style>
        <div className="cp-tabs">
          <button className={`cp-tab${tab==='certificates'?' on':''}`} onClick={()=>setTab('certificates')}>Event Certificates</button>
          <button className={`cp-tab${tab==='letters'?' on':''}`} onClick={()=>setTab('letters')}>Staff Letters</button>
        </div>
        {tab === 'certificates' ? <EventCertificates apiBase={apiBase}/> : <LetterBuilder/>}
      </div>
    );
  };
}

function EventCertificates({ apiBase }) {
  const [eventName, setEventName]   = useState('');
  const [achievement, setAchievement] = useState('');
  const [target, setTarget]         = useState('class'); // 'class' | 'section' | 'student'
  const [classes, setClasses]       = useState([]);
  const [classId, setClassId]       = useState('');
  const [section, setSection]       = useState('');
  const [issueDate, setIssueDate]   = useState(todayStr());
  const [studentQ, setStudentQ]     = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [pickedStudent, setPickedStudent]   = useState(null);
  const [generating, setGenerating] = useState(false);
  const [lastBatch, setLastBatch]   = useState(null);
  const [school, setSchool]         = useState({});
  const [history, setHistory]       = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get(`${apiBase}/classes`).then(r => setClasses(r.data?.classes || r.data || [])).catch(() => {});
    api.get('/settings/school').then(r => setSchool(r?.data || {})).catch(() => {});
    loadHistory();
  }, []);

  const loadHistory = () => {
    setLoadingHistory(true);
    api.get(`${apiBase}/certificates`).then(r => setHistory(r.data || [])).catch(() => {}).finally(() => setLoadingHistory(false));
  };

  useEffect(() => {
    if (!studentQ.trim()) { setStudentResults([]); return; }
    const t = setTimeout(() => {
      api.get(`${apiBase}/certificates/students?search=${encodeURIComponent(studentQ)}`).then(r => setStudentResults(r.data||[])).catch(()=>{});
    }, 250);
    return () => clearTimeout(t);
  }, [studentQ]);

  const selectedClass = classes.find(c => String(c._id||c.id) === String(classId));
  const sections = selectedClass?.sections || [];

  const generate = async () => {
    if (!eventName.trim()) { toast.error('Event name is required'); return; }
    if (target === 'student' && !pickedStudent) { toast.error('Pick a student'); return; }
    if (target !== 'student' && !classId) { toast.error('Pick a class'); return; }
    if (target === 'section' && !section) { toast.error('Pick a section'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post(`${apiBase}/certificates/generate`, {
        eventName, target, classId: classId || undefined, section: section || undefined,
        studentId: pickedStudent?._id, issueDate,
      });
      toast.success(data.message);
      setLastBatch(data);
      loadHistory();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to generate'); }
    finally { setGenerating(false); }
  };

  const printBatch = () => {
    if (!lastBatch) return;
    const html = buildCertificatePrintHTML({
      eventName: lastBatch.eventName, achievementText: achievement,
      schoolName: school.name, issueDate: lastBatch.issueDate, recipients: lastBatch.recipients,
    });
    const win = window.open('', '_blank', 'width=1000,height=750');
    win.document.write(html);
    win.document.close();
  };

  return (
    <div className="cp"><style>{css}</style>
      <div className="cp-header">
        <div className="cp-title">Certificates</div>
        <div className="cp-sub">Generate event certificates — result day, sports week, or any occasion</div>
      </div>

      <div className="layout">
        <div className="panel">
          <div className="form-field">
            <label className="form-label">Event Name *</label>
            <input className="form-input" placeholder="e.g. Annual Sports Week 2026" value={eventName} onChange={e=>setEventName(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="form-label">Achievement Text <span style={{fontWeight:400,textTransform:'none',color:'#b0b5cc'}}>(optional)</span></label>
            <textarea className="form-textarea" rows={2} placeholder="e.g. for winning 1st position in the 100m race" value={achievement} onChange={e=>setAchievement(e.target.value)} />
          </div>

          <label className="form-label" style={{marginBottom:6,display:'block'}}>Give To</label>
          <div className="target-tabs">
            <button className={`target-tab${target==='class'?' active':''}`} onClick={()=>setTarget('class')}>Whole Class</button>
            <button className={`target-tab${target==='section'?' active':''}`} onClick={()=>setTarget('section')}>Specific Section</button>
            <button className={`target-tab${target==='student'?' active':''}`} onClick={()=>setTarget('student')}>One Student</button>
          </div>

          {target !== 'student' ? (
            <>
              <div className="form-field">
                <label className="form-label">Class</label>
                <select className="form-select" value={classId} onChange={e=>{setClassId(e.target.value); setSection('');}}>
                  <option value="">— Select class —</option>
                  {classes.map(c => <option key={c._id||c.id} value={c._id||c.id}>{c.name}</option>)}
                </select>
              </div>
              {target === 'section' && (
                <div className="form-field">
                  <label className="form-label">Section</label>
                  <select className="form-select" value={section} onChange={e=>setSection(e.target.value)}>
                    <option value="">— Select section —</option>
                    {sections.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </>
          ) : (
            <div className="form-field">
              <label className="form-label">Student</label>
              {pickedStudent ? (
                <div className="picked-chip">{pickedStudent.name} — {pickedStudent.className} {pickedStudent.section}
                  <button type="button" onClick={()=>setPickedStudent(null)} style={{border:'none',background:'none',cursor:'pointer',color:'#5b21b6'}}>✕</button>
                </div>
              ) : (
                <>
                  <input className="form-input" placeholder="Search by name or admission no…" value={studentQ} onChange={e=>setStudentQ(e.target.value)} />
                  {studentResults.length>0 && <div className="suggest-list">{studentResults.map(s=>(
                    <div key={s._id} className="suggest-item" onClick={()=>{setPickedStudent(s); setStudentResults([]); setStudentQ('');}}>{s.name} — {s.className} {s.section}</div>
                  ))}</div>}
                </>
              )}
            </div>
          )}

          <div className="form-field">
            <label className="form-label">Issue Date</label>
            <input type="date" className="form-input" value={issueDate} onChange={e=>setIssueDate(e.target.value)} />
          </div>

          <button className="btn btn-purple" onClick={generate} disabled={generating}>
            <AcademicCapIcon/>{generating ? 'Generating…' : 'Generate Certificates'}
          </button>
        </div>

        <div>
          {lastBatch && (
            <div className="preview-card">
              <div className="cert-frame">
                <div className="cert-title">Certificate of Achievement</div>
                <div className="cert-sub">{lastBatch.eventName}</div>
                <div className="cert-name">{lastBatch.recipients[0]?.name}{lastBatch.recipients.length>1 ? ` + ${lastBatch.recipients.length-1} more` : ''}</div>
                <div className="cert-body">{achievement || `in recognition of their participation and achievement on the occasion of ${lastBatch.eventName}.`}</div>
              </div>
              <div className="recip-list">
                {lastBatch.recipients.map(r => (
                  <div key={r._id} className="recip-row"><span>{r.name}</span><span style={{color:'#9094a8'}}>{r.className} {r.section}</span></div>
                ))}
              </div>
              <button className="btn btn-purple" style={{marginTop:14}} onClick={printBatch}><PrinterIcon/>Print / Save as PDF ({lastBatch.recipients.length})</button>
            </div>
          )}

          <div className="history-panel">
            {loadingHistory ? <div className="loading"><div className="spinner"/>Loading…</div>
            : history.length === 0 ? <div className="empty">No certificates issued yet.</div>
            : (() => {
                const grouped = {};
                history.forEach(h => {
                  const key = `${h.eventName}__${h.issueDate}`;
                  if (!grouped[key]) grouped[key] = { eventName: h.eventName, issueDate: h.issueDate, count: 0 };
                  grouped[key].count++;
                });
                const rows = Object.values(grouped);
                return (
                  <div style={{overflowX:'auto'}}>
                    <table className="tbl">
                      <thead><tr><th>Event</th><th>Date</th><th>Recipients</th></tr></thead>
                      <tbody>{rows.map((r,i) => (
                        <tr key={i}><td style={{fontWeight:600,textAlign:'left',paddingLeft:20}}>{r.eventName}</td><td>{fmtDate(r.issueDate)}</td><td>{r.count}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                );
              })()}
          </div>
        </div>
      </div>
    </div>
  );
}
